import { PlanetName, ZodiacSign, PlanetPosition, CompleteKundali, Language, ShadbalaItem } from '../types/kundali';
import { getFunctionalNature } from './astrologyRules';
import { ZODIAC_LORDS, ZODIAC_SIGNS } from './vedicEngine';

export type DignityLevel =
  | 'Exalted'
  | 'Moolatrikona'
  | 'Own House'
  | 'Great Friend'
  | 'Friend'
  | 'Neutral'
  | 'Enemy'
  | 'Great Enemy'
  | 'Debilitated';

export interface NakshatraImpactDetail {
  nakshatra: string;
  nakshatraIndex: number;
  pada: number;
  lord: PlanetName;
  deity: string;
  deityHi: string;
  shakti: string;
  shaktiHi: string;
  navtaraType: string;
  navtaraTypeHi: string;
  isTarabalaFavorable: boolean;
  impactDescriptionEn: string;
  impactDescriptionHi: string;
}

export interface PlanetaryRelationshipsInfo {
  naturalFriends: PlanetName[];
  naturalNeutrals: PlanetName[];
  naturalEnemies: PlanetName[];
  temporalFriends: PlanetName[];
  temporalEnemies: PlanetName[];
  dispositor: PlanetName;
  compoundWithDispositor: 'Great Friend' | 'Friend' | 'Neutral' | 'Enemy' | 'Great Enemy';
  compoundWithDispositorHi: string;
  lagnaLord: PlanetName;
  lagnaLordRelationship: {
    natural: 'Friend' | 'Neutral' | 'Enemy';
    temporal: 'Friend' | 'Enemy';
    compound: 'Great Friend' | 'Friend' | 'Neutral' | 'Enemy' | 'Great Enemy';
    compoundHi: string;
  };
  ownSigns: ZodiacSign[];
  moolatrikonaInfo?: string;
  moolatrikonaInfoHi?: string;
  exaltationInfo: { sign: ZodiacSign; degree: number };
  debilitationInfo: { sign: ZodiacSign; degree: number };
}

export interface KarakaDetail {
  sthiraNameEn: string;
  sthiraNameHi: string;
  sthiraSigEn: string;
  sthiraSigHi: string;
  jaiminiCode?: 'AK' | 'AmK' | 'BK' | 'MK' | 'PK' | 'GK' | 'DK';
  jaiminiNameEn?: string;
  jaiminiNameHi?: string;
  jaiminiSignificanceEn?: string;
  jaiminiSignificanceHi?: string;
}

export interface EnhancedPlanetaryDignity {
  planet: PlanetName;
  sign: ZodiacSign;
  degreeInSign: number;
  formattedDegree: string;
  house: number;
  isRetrograde: boolean;
  dignity: DignityLevel;
  dignityLabelEn: string;
  dignityLabelHi: string;
  dignityScore: number; // 0 to 100
  deepDegreeNote?: string;
  isDeeplyExalted: boolean;
  isDeeplyDebilitated: boolean;
  isDebilitated: boolean;
  isExalted: boolean;
  neechaBhanga: {
    isActive: boolean;
    reasons: string[];
    descriptionEn: string;
    descriptionHi: string;
  };
  karaka: KarakaDetail;
  naturalNature: 'Natural Benefic' | 'Natural Malefic';
  naturalNatureHi: string;
  functionalNature: 'Functional Benefic' | 'Functional Malefic' | 'Neutral';
  functionalNatureHi: string;
  isYogakaraka: boolean;
  isLagnaLord: boolean;
  natureRoleDescription: string;
  nakshatraImpact: NakshatraImpactDetail;
  relationships: PlanetaryRelationshipsInfo;
  shadbala?: ShadbalaItem;
  d9Sign?: ZodiacSign;
  d9Lord?: PlanetName;
  isVargottama?: boolean;
  isPushkarNavamsha?: boolean;
  isPushkarBhaga?: boolean;
  d9Dignity?: string;
}

// Classical Natural Friendships
export const NATURAL_FRIENDSHIPS: Record<
  PlanetName,
  { friends: PlanetName[]; neutrals: PlanetName[]; enemies: PlanetName[] }
> = {
  Sun: {
    friends: ['Moon', 'Mars', 'Jupiter'],
    neutrals: ['Mercury'],
    enemies: ['Venus', 'Saturn', 'Rahu', 'Ketu']
  },
  Moon: {
    friends: ['Sun', 'Mercury'],
    neutrals: ['Mars', 'Jupiter', 'Venus', 'Saturn'],
    enemies: ['Rahu', 'Ketu']
  },
  Mars: {
    friends: ['Sun', 'Moon', 'Jupiter'],
    neutrals: ['Venus', 'Saturn'],
    enemies: ['Mercury', 'Rahu', 'Ketu']
  },
  Mercury: {
    friends: ['Sun', 'Venus'],
    neutrals: ['Mars', 'Jupiter', 'Saturn'],
    enemies: ['Moon', 'Rahu', 'Ketu']
  },
  Jupiter: {
    friends: ['Sun', 'Moon', 'Mars'],
    neutrals: ['Saturn'],
    enemies: ['Mercury', 'Venus', 'Rahu', 'Ketu']
  },
  Venus: {
    friends: ['Mercury', 'Saturn', 'Rahu', 'Ketu'],
    neutrals: ['Mars', 'Jupiter'],
    enemies: ['Sun', 'Moon']
  },
  Saturn: {
    friends: ['Mercury', 'Venus', 'Rahu'],
    neutrals: ['Jupiter'],
    enemies: ['Sun', 'Moon', 'Mars', 'Ketu']
  },
  Rahu: {
    friends: ['Venus', 'Saturn', 'Mercury'],
    neutrals: ['Jupiter'],
    enemies: ['Sun', 'Moon', 'Mars']
  },
  Ketu: {
    friends: ['Mars', 'Jupiter', 'Venus'],
    neutrals: ['Mercury'],
    enemies: ['Sun', 'Moon', 'Saturn']
  },
  Ascendant: {
    friends: [],
    neutrals: [],
    enemies: []
  }
};

// Own Signs Definition
export const PLANET_OWN_SIGNS: Record<PlanetName, ZodiacSign[]> = {
  Sun: ['Leo'],
  Moon: ['Cancer'],
  Mars: ['Aries', 'Scorpio'],
  Mercury: ['Gemini', 'Virgo'],
  Jupiter: ['Sagittarius', 'Pisces'],
  Venus: ['Taurus', 'Libra'],
  Saturn: ['Capricorn', 'Aquarius'],
  Rahu: ['Aquarius'],
  Ketu: ['Scorpio'],
  Ascendant: []
};

export function getPanchadhaMaitriCompound(
  naturalRel: 'Friend' | 'Neutral' | 'Enemy',
  temporalRel: 'Friend' | 'Enemy'
): 'Great Friend' | 'Friend' | 'Neutral' | 'Enemy' | 'Great Enemy' {
  if (naturalRel === 'Friend' && temporalRel === 'Friend') return 'Great Friend';
  if ((naturalRel === 'Friend' && temporalRel === 'Enemy') || (naturalRel === 'Enemy' && temporalRel === 'Friend'))
    return 'Neutral';
  if (naturalRel === 'Neutral' && temporalRel === 'Friend') return 'Friend';
  if (naturalRel === 'Neutral' && temporalRel === 'Enemy') return 'Enemy';
  if (naturalRel === 'Enemy' && temporalRel === 'Enemy') return 'Great Enemy';
  return 'Neutral';
}

export function getCompoundRelationshipHindi(
  compound: 'Great Friend' | 'Friend' | 'Neutral' | 'Enemy' | 'Great Enemy'
): string {
  switch (compound) {
    case 'Great Friend':
      return 'अतिमित्र (परम सहयोगी)';
    case 'Friend':
      return 'मित्र (सहयोगी)';
    case 'Neutral':
      return 'सम (तटस्थ)';
    case 'Enemy':
      return 'शत्रु (विरोधी)';
    case 'Great Enemy':
      return 'अतिशत्रु (परम विरोधी)';
  }
}

// Deep Exaltation & Debilitation Degrees
const DEEP_POINTS: Record<PlanetName, { exaltSign: ZodiacSign; exaltDeg: number; debSign: ZodiacSign; debDeg: number }> = {
  Sun: { exaltSign: 'Aries', exaltDeg: 10, debSign: 'Libra', debDeg: 10 },
  Moon: { exaltSign: 'Taurus', exaltDeg: 3, debSign: 'Scorpio', debDeg: 3 },
  Mars: { exaltSign: 'Capricorn', exaltDeg: 28, debSign: 'Cancer', debDeg: 28 },
  Mercury: { exaltSign: 'Virgo', exaltDeg: 15, debSign: 'Pisces', debDeg: 15 },
  Jupiter: { exaltSign: 'Cancer', exaltDeg: 5, debSign: 'Capricorn', debDeg: 5 },
  Venus: { exaltSign: 'Pisces', exaltDeg: 27, debSign: 'Virgo', debDeg: 27 },
  Saturn: { exaltSign: 'Libra', exaltDeg: 20, debSign: 'Aries', debDeg: 20 },
  Rahu: { exaltSign: 'Taurus', exaltDeg: 20, debSign: 'Scorpio', debDeg: 20 },
  Ketu: { exaltSign: 'Scorpio', exaltDeg: 20, debSign: 'Taurus', debDeg: 20 },
  Ascendant: { exaltSign: 'Aries', exaltDeg: 0, debSign: 'Libra', debDeg: 0 }
};

// Moolatrikona Definitions
const MOOLATRIKONA: Partial<Record<PlanetName, { sign: ZodiacSign; startDeg: number; endDeg: number }>> = {
  Sun: { sign: 'Leo', startDeg: 0, endDeg: 20 },
  Moon: { sign: 'Taurus', startDeg: 3, endDeg: 30 },
  Mars: { sign: 'Aries', startDeg: 0, endDeg: 12 },
  Mercury: { sign: 'Virgo', startDeg: 15, endDeg: 20 },
  Jupiter: { sign: 'Sagittarius', startDeg: 0, endDeg: 10 },
  Venus: { sign: 'Libra', startDeg: 0, endDeg: 15 },
  Saturn: { sign: 'Aquarius', startDeg: 0, endDeg: 20 }
};

// 27 Nakshatras Vedic Knowledge Matrix
const NAKSHATRA_METADATA: Record<string, {
  deityEn: string;
  deityHi: string;
  shaktiEn: string;
  shaktiHi: string;
  coreThemeEn: string;
  coreThemeHi: string;
}> = {
  'Ashwini': {
    deityEn: 'Ashwini Kumaras (Physicians of Gods)',
    deityHi: 'अश्विनी कुमार (दिव्य वैद्य)',
    shaktiEn: 'Swift healing, miraculous beginnings & dynamism',
    shaktiHi: 'शीघ्र आरोग्य, नवीन आरंभ व असीम गतिशीलता',
    coreThemeEn: 'Bestows sharp intellect, pioneering energy, agility, and swift problem solving.',
    coreThemeHi: 'नेतृत्व क्षमता, त्वरित कार्य निष्पादन एवं नवीन पहलों में सफलता प्रदान करता है।'
  },
  'Bharani': {
    deityEn: 'Yama (Lord of Dharma & Transformation)',
    deityHi: 'यमराज (धर्म व रूपांतरण के देवता)',
    shaktiEn: 'Rebirth, profound endurance & cleansing',
    shaktiHi: 'पुनर्जन्म, गहन सहनशीलता व कर्म शोधन',
    coreThemeEn: 'Gives deep creative passion, capacity to withstand crisis, and relentless perseverance.',
    coreThemeHi: 'गहन सृजन शक्ति, कठिन परिस्थितियों से उबरने का आत्मबल और सत्यनिष्ठा।'
  },
  'Krittika': {
    deityEn: 'Agni (God of Sacred Fire)',
    deityHi: 'अग्नि देव (पवित्र पावक)',
    shaktiEn: 'Purifying fire, sharp discernment & incisiveness',
    shaktiHi: 'शुद्धिकरण, प्रखर तेज व तीक्ष्ण विवेक',
    coreThemeEn: 'Cuts through illusions; imparts courage, authoritative speech, and digestive/intellectual fire.',
    coreThemeHi: 'सत्य को उजागर करने की क्षमता, ओजस्वी वाणी एवं दृढ़ निश्चय।'
  },
  'Rohini': {
    deityEn: 'Brahma / Prajapati (The Creator)',
    deityHi: 'ब्रह्मा / प्रजापति (सृष्टिकर्ता)',
    shaktiEn: 'Fertility, artistic charm & material manifestation',
    shaktiHi: 'समृद्धि, कलात्मक आकर्षण व भौतिक प्रगति',
    coreThemeEn: 'Nourishes growth, personal magnetism, aesthetic brilliance, and sustainable prosperity.',
    coreThemeHi: 'आकर्षण, रचनात्मक कला, व्यापारिक वृद्धि एवं सुख-समृद्धि में वृद्धि।'
  },
  'Mrigashira': {
    deityEn: 'Soma (Moon / Nectar of Immortality)',
    deityHi: 'सोम (चंद्रमा / अमृत तत्व)',
    shaktiEn: 'Search for truth, curiosity & spiritual seeking',
    shaktiHi: 'सत्य की खोज, जिज्ञासा व संवेदनशीलता',
    coreThemeEn: 'Instills exploratory mindset, research abilities, gentle charm, and travel inclination.',
    coreThemeHi: 'अनुसंधान प्रियता, मृदु व्यवहार, बौद्धिक जिज्ञासा एवं बहुमुखी प्रतिभा।'
  },
  'Ardra': {
    deityEn: 'Rudra (Storm & Destructive Transformation)',
    deityHi: 'रुद्र (परिवर्तन व संहारक शक्ति)',
    shaktiEn: 'Effort through destruction of obstacles & breakthrough',
    shaktiHi: 'बाधाओं का विनाश व आंधी के बाद की स्पष्टता',
    coreThemeEn: 'Channels intense emotional depth, innovative breakthroughs, and resilience after adversity.',
    coreThemeHi: 'गंभीर चिंतन, क्रांतिकारी विचार एवं संकटों को अवसर में बदलने की क्षमता।'
  },
  'Punarvasu': {
    deityEn: 'Aditi (Mother of the Gods / Boundless Space)',
    deityHi: 'अदिति (देवमाता / असीम आकाश)',
    shaktiEn: 'Renewal of light, return of prosperity & protection',
    shaktiHi: 'पुनः प्रकाश प्राप्ति, समृद्धि की वापसी व सुरक्षा',
    coreThemeEn: 'Brings bounce-back resilience, philosophical wisdom, benevolence, and renewed success.',
    coreThemeHi: 'पुनरुद्धार, परोपकार, उच्च संस्कार एवं खोए हुए मान-सम्मान की पुनः प्राप्ति।'
  },
  'Pushya': {
    deityEn: 'Brihaspati (Guru of the Gods)',
    deityHi: 'बृहस्पति (देवगुरु)',
    shaktiEn: 'Supreme nourishment, spiritual mastery & benevolence',
    shaktiHi: 'परम पोषण, आध्यात्मिक ज्ञान व सात्विक वृद्धि',
    coreThemeEn: 'Most auspicious asterism; confers dharmic conduct, mentorship wisdom, and steady wealth.',
    coreThemeHi: 'धर्मपरायणता, गुरु तुल्य मार्गदर्शन, स्थिर आर्थिक उन्नति व समाज में आदर।'
  },
  'Ashlesha': {
    deityEn: 'Sarpa / Nagas (Serpent Deities)',
    deityHi: 'नाग देव (कुंडलिनी व गूढ़ विद्या)',
    shaktiEn: 'Kundalini awakening, intense insight & strategic defense',
    shaktiHi: 'कुंडलिनी जागरण, गूढ़ अंतर्दृष्टि व कूटनीति',
    coreThemeEn: 'Endows deep psychological penetration, hypnotic intuition, and strategic mastery.',
    coreThemeHi: 'रहस्यों को भांपने की अद्भुत क्षमता, कूटनीतिक चातुर्य एवं गहन मनोवैज्ञानिक समझ।'
  },
  'Magha': {
    deityEn: 'Pitris (Ancestral Forefathers)',
    deityHi: 'पितर (पूर्वज)',
    shaktiEn: 'Lineage blessing, royal authority & leadership',
    shaktiHi: 'पितृ आशीर्वाद, राजसी सम्मान व नेतृत्व',
    coreThemeEn: 'Confers dignity, executive authority, ancestral pride, and societal prominence.',
    coreThemeHi: 'उच्च पद-प्रतिष्ठा, नेतृत्व गुण, पूर्वजों के पुण्य व स्वाभिमान।'
  },
  'Purva Phalguni': {
    deityEn: 'Bhaga (God of Fortune & Marital Bliss)',
    deityHi: 'भग (सौभाग्य व दांपत्य सुख)',
    shaktiEn: 'Creative enjoyment, celebration & loving union',
    shaktiHi: 'कलात्मक आनंद, उत्सव व प्रेम-संबंध',
    coreThemeEn: 'Brings aesthetic romance, theatrical talent, generous hospitality, and relaxed comfort.',
    coreThemeHi: 'विलासिता, ललित कलाएं, दांपत्य सामंजस्य एवं सामाजिक लोकप्रियता।'
  },
  'Uttara Phalguni': {
    deityEn: 'Aryaman (God of Patronage & Noble Contracts)',
    deityHi: 'अर्यमा (सच्ची मित्रता व महान अनुबंध)',
    shaktiEn: 'Generosity, enduring partnerships & noble service',
    shaktiHi: 'उदारता, दीर्घकालिक साझेदारी व कर्तव्यनिष्ठा',
    coreThemeEn: 'Enhances executive reliability, philanthropic leadership, and steadfast alliances.',
    coreThemeHi: 'विश्वसनीयता, प्रशासनिक कुशलता, स्थिर मित्र संबंध एवं कर्तव्यपरायणता।'
  },
  'Hasta': {
    deityEn: 'Savitr (Sun as the Golden Creator)',
    deityHi: 'सविता (ज्ञान व प्रकाश के सूर्य देव)',
    shaktiEn: 'Skillful craftsmanship, manifestation & healing hands',
    shaktiHi: 'हस्त कौशल, कार्य सिद्धि व जादुई दक्षता',
    coreThemeEn: 'Gives manual dexterities, mercantile acumen, witty humor, and precise execution.',
    coreThemeHi: 'वाणिज्यिक निपुणता, तकनीकी कौशल, हाजिरजवाबी एवं कार्य में सूक्ष्म परिशुद्धता।'
  },
  'Chitra': {
    deityEn: 'Tvashtar / Vishwakarma (Divine Architect)',
    deityHi: 'विश्वकर्मा (दिव्य शिल्पी)',
    shaktiEn: 'Visual beauty, engineering brilliance & structural design',
    shaktiHi: 'वास्तु-शिल्प, आकर्षक रूप-रंग व संरचनात्मक प्रतिभा',
    coreThemeEn: 'Sparks architectural flair, magnetic elegance, jewel-like sparkle, and design excellence.',
    coreThemeHi: 'सृजनात्मक डिजाइन, सौंदर्य बोध, इंजीनियरिंग क्षमता एवं प्रभावशाली व्यक्तित्व।'
  },
  'Swati': {
    deityEn: 'Vayu (God of Wind & Prana)',
    deityHi: 'वायु देव (प्राण व गतिशीलता)',
    shaktiEn: 'Independence, flexibility, dispersion & commerce',
    shaktiHi: 'स्वतंत्रता, लचीलापन, व्यापार व संतुलन',
    coreThemeEn: 'Fosters diplomatic tact, freedom-loving enterprise, global trade, and intellectual agility.',
    coreThemeHi: 'कूटनीति, स्वतंत्र व्यवसाय, त्वरित अनुकूलनशीलता एवं बौद्धिक विस्तार।'
  },
  'Vishakha': {
    deityEn: 'Indra & Agni (Supreme Alliance of Power & Fire)',
    deityHi: 'इन्द्राग्नि (शक्ति व तेज का महामिलन)',
    shaktiEn: 'Single-pointed ambition, triumph & relentless triumph',
    shaktiHi: 'एकाग्र लक्ष्य, विजय प्राप्ति व अदम्य संकल्प',
    coreThemeEn: 'Provides targeted ambition, triumph over rivals, relentless goal focus, and charisma.',
    coreThemeHi: 'लक्ष्य भेदने की क्षमता, प्रतिस्पर्धियों पर विजय, दृढ़ इच्छाशक्ति एवं प्रभावशाली वाणी।'
  },
  'Anuradha': {
    deityEn: 'Mitra (God of Friendship & Devotion)',
    deityHi: 'मित्र देव (सद्भाव, मित्रता व भक्ति)',
    shaktiEn: 'Devotion, cooperative success & international travel',
    shaktiHi: 'निष्ठा, सामूहिक सफलता व वैश्विक संबंध',
    coreThemeEn: 'Cultivates warm networking, emotional endurance, occult loyalty, and foreign prosperity.',
    coreThemeHi: 'सद्भावना, विदेशी संपर्कों से लाभ, आध्यात्मिक भक्ति एवं मजबूत टीमवर्क।'
  },
  'Jyeshtha': {
    deityEn: 'Indra (King of the Gods)',
    deityHi: 'इंद्र देव (देवराज)',
    shaktiEn: 'Courage to protect, senior authority & triumph in battle',
    shaktiHi: 'वरिष्ठता, सुरक्षात्मक सामर्थ्य व विजय',
    coreThemeEn: 'Confers protective mastery, seniority status, keen detective instincts, and pride.',
    coreThemeHi: 'नेतृत्व क्षमता, अधिकार संपन्नता, सतर्कता एवं कठिन चुनौतियों पर नियंत्रण।'
  },
  'Mula': {
    deityEn: 'Nirriti (Goddess of Dissolution / Root Truth)',
    deityHi: 'निरृति (मूल सत्य व बंधन मुक्ति)',
    shaktiEn: 'Uprooting falsehood, deep research & core truth',
    shaktiHi: 'जड़ से समाधान, अनुसंधान व असत्य का निवारण',
    coreThemeEn: 'Facilitates deep root-cause diagnosis, spiritual detachment, and transformative knowledge.',
    coreThemeHi: 'गहन अनुसंधान, मौलिक चिंतन, समस्याओं के मूल तक पहुंचना एवं दार्शनिक ज्ञान।'
  },
  'Purva Ashadha': {
    deityEn: 'Apas (Cosmic Waters / Primordial Oceans)',
    deityHi: 'आपः (दिव्य जल तत्व)',
    shaktiEn: 'Invincibility, purification & persuasive eloquence',
    shaktiHi: 'अजेयता, ओजस्वी वक्तृत्व व मनोबल',
    coreThemeEn: 'Imparts unshakable confidence, captivating oratory, philosophical depth, and victory.',
    coreThemeHi: 'अटूट आत्मविश्वास, प्रभावशाली जनसंचार, नीतिगत सफलता एवं प्रतिष्ठा।'
  },
  'Uttara Ashadha': {
    deityEn: 'Vishwadevas (Universal Cosmic Principles)',
    deityHi: 'विश्वेदेवा (सार्वभौमिक सत्य व धर्म)',
    shaktiEn: 'Permanent victory, righteous leadership & structural honor',
    shaktiHi: 'स्थायी विजय, सत्यनिष्ठ नेतृत्व व सर्वमान्य सम्मान',
    coreThemeEn: 'Bestows long-lasting achievements, unblemished integrity, statesmanship, and high stature.',
    coreThemeHi: 'दीर्घकालिक सफलता, लोक कल्याणकारी नेतृत्व, निष्कलंक छवि व दृढ़ संयम।'
  },
  'Shravana': {
    deityEn: 'Vishnu (The All-Pervading Preserver)',
    deityHi: 'भगवान विष्णु (जगतपालक)',
    shaktiEn: 'Active listening, oral wisdom, learning & preservation',
    shaktiHi: 'श्रवण शक्ति, ज्ञानार्जन, संरक्षण व यश',
    coreThemeEn: 'Enhances scholarly listening, academic prestige, counseling wisdom, and widespread fame.',
    coreThemeHi: 'उत्कृष्ट श्रोता, शास्त्रीय ज्ञान, परामर्श देने की क्षमता एवं समाज में सुकीर्ति।'
  },
  'Dhanishta': {
    deityEn: 'Ashta Vasus (The 8 Elemental Deities of Wealth)',
    deityHi: 'अष्ट वसु (समृद्धि व प्रकृति के 8 तत्व)',
    shaktiEn: 'Abundance, rhythm, musical mastery & martial bravery',
    shaktiHi: 'अखंड लक्ष्मी, लय-ताल, संगीत व शौर्य',
    coreThemeEn: 'Brings immense material wealth, rhythm/music sensitivity, athletic prowess, and organizational skill.',
    coreThemeHi: 'धन संपदा का आगमन, कलात्मक लय, संगठन क्षमता एवं पराक्रम।'
  },
  'Shatabhisha': {
    deityEn: 'Varuna (Lord of Cosmic Waters & Healing)',
    deityHi: 'वरुण देव (दिव्य औषधि व ब्रह्मांडीय न्याय)',
    shaktiEn: '100 physicians, mystical healing, research & veil of secrecy',
    shaktiHi: 'शत-भिषज (सौ औषधियां), रहस्यमयी आरोग्य व गूढ़ चिंतन',
    coreThemeEn: 'Bestows diagnostic vision, astronomical/scientific insight, solitude power, and unconventional remedies.',
    coreThemeHi: 'गूढ़ वैज्ञानिक दृष्टिकोण, चिकित्सा व आरोग्य में विशेष सफलता, एकांत साधना।'
  },
  'Purva Bhadrapada': {
    deityEn: 'Aja Ekapada (The Cosmic Fire Dragon / One-Footed Lord)',
    deityHi: 'अज एकपाद (दिव्य तपोबल व एकाग्रता)',
    shaktiEn: 'Ascetic discipline, intense spiritual fire & occult vision',
    shaktiHi: 'कठोर तपस्या, एकाग्र साधना व आध्यात्मिक तेज',
    coreThemeEn: 'Provides visionary intuition, transformative intellect, profound occult focus, and high ideals.',
    coreThemeHi: 'उच्च आदर्श, पारलौकिक अंतर्दृष्टि, त्याग भाव एवं गहन अन्वेषण शक्ति।'
  },
  'Uttara Bhadrapada': {
    deityEn: 'Ahirbudhnya (Serpent of the Deep Waters)',
    deityHi: 'अहिर्बुध्न्य (समुद्र की गहराइयों का नाग)',
    shaktiEn: 'Deep stillness, profound wisdom, endurance & stability',
    shaktiHi: 'गंभीर शांति, धैर्य, अगाध ज्ञान व स्थिरता',
    coreThemeEn: 'Cultivates serene patience, benevolent charity, grounded spirituality, and emotional restraint.',
    coreThemeHi: 'अगाध गंभीरता, निस्वार्थ सेवा, मानसिक संतुलन एवं आध्यात्मिक परिपक्वता।'
  },
  'Revati': {
    deityEn: 'Pushan (The Divine Shepherd / Protector of Journeys)',
    deityHi: 'पूषा (मार्गदर्शक व रक्षक देवता)',
    shaktiEn: 'Safe transit, boundless nourishment, prosperity & completion',
    shaktiHi: 'सुरक्षित यात्रा, सर्वतोमुखी पोषण व मोक्ष तत्व',
    coreThemeEn: 'Blesses with divine protection during transitions, gentle compassion, wealth preservation, and spiritual liberation.',
    coreThemeHi: 'समस्त कार्यों की निर्विघ्न पूर्णता, दयालु स्वभाव, धन संरक्षण व आध्यात्मिक शांति।'
  }
};

// Navtara 9-fold Classification
const NAVTARA_LIST: { nameEn: string; nameHi: string; isFavorable: boolean }[] = [
  { nameEn: 'Janma (Identity & Foundation)', nameHi: 'जन्म तारा (स्वरूप व आधार)', isFavorable: true },
  { nameEn: 'Sampat (Wealth & Prosperity)', nameHi: 'सम्पत् तारा (धन व समृद्धि)', isFavorable: true },
  { nameEn: 'Vipat (Crisis & Caution)', nameHi: 'विपत् तारा (सावधानी व बाधा)', isFavorable: false },
  { nameEn: 'Kshema (Wellbeing & Security)', nameHi: 'क्षेम तारा (आरोग्य व कल्याण)', isFavorable: true },
  { nameEn: 'Pratyak (Obstacles & Delays)', nameHi: 'प्रत्यरि तारा (प्रतिरोध व विलंब)', isFavorable: false },
  { nameEn: 'Sadhana (Success & Achievement)', nameHi: 'साधक तारा (कार्य सिद्धि व साधना)', isFavorable: true },
  { nameEn: 'Naidhana / Vadha (Intense Karma)', nameHi: 'वध / नैधन तारा (गहन कर्म शोधन)', isFavorable: false },
  { nameEn: 'Mitra (Friend & Comfort)', nameHi: 'मित्र तारा (सहयोग व सुख)', isFavorable: true },
  { nameEn: 'Parama Mitra (Supreme Fortune)', nameHi: 'परम मित्र तारा (परम कल्याण व भाग्य)', isFavorable: true }
];

export const STHIRA_KARAKAS: Record<PlanetName, { karakaEn: string; karakaHi: string; sigEn: string; sigHi: string }> = {
  Sun: {
    karakaEn: 'Atma & Pitri Karaka',
    karakaHi: 'आत्मा व पितृ कारक',
    sigEn: 'Soul purpose, Father, Vitality, Executive authority & Prestige',
    sigHi: 'आत्म तत्व, पिता, जीवन शक्ति, राजकीय सत्ता व प्रतिष्ठा'
  },
  Moon: {
    karakaEn: 'Manas & Matri Karaka',
    karakaHi: 'मन व मातृ कारक',
    sigEn: 'Mind, Mother, Emotional contentment, Nourishment & Fluid wealth',
    sigHi: 'मनोबल, माता, मानसिक शांति, पोषण व द्रव्य संपदा'
  },
  Mars: {
    karakaEn: 'Bhratri & Shakti Karaka',
    karakaHi: 'भ्रातृ व शक्ति कारक',
    sigEn: 'Siblings, Courage, Real estate/Land, Ambition & Vital force',
    sigHi: 'अनुज/भाई, शौर्य, भूमि-भवन, पराक्रम व तेज'
  },
  Mercury: {
    karakaEn: 'Buddhi & Vidya Karaka',
    karakaHi: 'बुद्धि व विद्या कारक',
    sigEn: 'Intellect, Speech, Commercial trade, Mathematics & Analytical acumen',
    sigHi: 'विवेक, वाणी, व्यापार-वाणिज्य, तर्क व संवाद कौशल'
  },
  Jupiter: {
    karakaEn: 'Jnana & Putra Karaka',
    karakaHi: 'ज्ञान व पुत्र कारक',
    sigEn: 'Higher wisdom, Children, Guru/Mentorship, Dharma & Divine luck',
    sigHi: 'दिव्य ज्ञान, संतान, गुरु कृपा, धर्म व सर्वतोमुखी सौभाग्य'
  },
  Venus: {
    karakaEn: 'Kalatra & Sukha Karaka',
    karakaHi: 'कलत्र व सुख कारक',
    sigEn: 'Spouse, Marital bliss, Vehicles, Creative arts & Sensory luxury',
    sigHi: 'जीवनसाथी, वैवाहिक आनंद, वाहन, ललित कला व ऐश्वर्य'
  },
  Saturn: {
    karakaEn: 'Ayush & Karma Karaka',
    karakaHi: 'आयु व कर्म कारक',
    sigEn: 'Longevity, Profession/Labor, Discipline, Mass influence & Perseverance',
    sigHi: 'आयु, आजीविका/कठिन कर्म, अनुशासन, जनसमूह व धैर्य'
  },
  Rahu: {
    karakaEn: 'Pitamaha & Maya Karaka',
    karakaHi: 'पितामह व माया कारक',
    sigEn: 'Unorthodox research, Foreign connections, Technology & Worldly drive',
    sigHi: 'गूढ़ शोध, विदेश संपर्क, नवप्रवर्तन व महत्वाकांक्षा'
  },
  Ketu: {
    karakaEn: 'Matamaha & Moksha Karaka',
    karakaHi: 'मातामह व मोक्ष कारक',
    sigEn: 'Spiritual liberation, Intuition, Detachment & Esoteric mastery',
    sigHi: 'मोक्ष, अंतर्ज्ञान, वैराग्य व आध्यात्मिक सिद्धि'
  },
  Ascendant: {
    karakaEn: 'Tanu Karaka',
    karakaHi: 'तनु कारक',
    sigEn: 'Self-identity, Physical constitution & Overall life trajectory',
    sigHi: 'आत्मस्वरूप, शारीरिक गठन व समग्र जीवन दिशा'
  }
};

/**
 * Calculates complete enhanced dignity, nature (natural/functional/yogakaraka),
 * and nakshatra impact for all planets in the chart.
 */
export function getEnhancedPlanetaryDignityList(
  kundali: CompleteKundali,
  lang: Language = 'en'
): EnhancedPlanetaryDignity[] {
  const { planets, houses } = kundali;
  const lagnaSignIndex = houses[0]?.signIndex ?? 0;
  const moonPlanet = planets.find((p) => p.name === 'Moon');
  const natalMoonNakIdx = moonPlanet ? moonPlanet.nakshatraIndex ?? 0 : 0;

  // Compute Jaimini 7-Chara Karakas (Sun to Saturn ranked by degree descending)
  const physical7Planets = ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'] as PlanetName[];
  const sortedPhysical = planets
    .filter((p) => physical7Planets.includes(p.name as PlanetName))
    .sort((a, b) => (b.degreeInSign ?? 0) - (a.degreeInSign ?? 0));

  const jaiminiRoles: Record<
    PlanetName,
    { code: 'AK' | 'AmK' | 'BK' | 'MK' | 'PK' | 'GK' | 'DK'; nameEn: string; nameHi: string; sigEn: string; sigHi: string }
  > = {} as any;

  const jaiminiOrder = [
    { code: 'AK' as const, nameEn: 'Atmakaraka (AK)', nameHi: 'आत्माकारक (AK)', sigEn: 'Prime Soul indicator, Core spiritual destiny & Life mission', sigHi: 'आत्मा का मुख्य कारक, मूल आध्यात्मिक प्रारब्ध व जीवन उद्देश्य' },
    { code: 'AmK' as const, nameEn: 'Amatyakaraka (AmK)', nameHi: 'अमात्यकारक (AmK)', sigEn: 'Prime Minister of chart, Career status, Intellect & Ambition', sigHi: 'कुंडली का मुख्य सलाहकार, पद-प्रतिष्ठा, आजीविका व बुद्धि' },
    { code: 'BK' as const, nameEn: 'Bhratrikaraka (BK)', nameHi: 'भ्रातृकारक (BK)', sigEn: 'Siblings, Mentors, Gurus & Spiritual guides', sigHi: 'सहोदर/भाई, गुरुजन, मार्गदर्शक व आत्मिक प्रेरणा' },
    { code: 'MK' as const, nameEn: 'Matrikaraka (MK)', nameHi: 'मातृकारक (MK)', sigEn: 'Mother, Emotional foundation, Vehicles, Education & Inner peace', sigHi: 'माता, भावनात्मक आधार, वाहन-संपत्ति, विद्या व गृह सुख' },
    { code: 'PK' as const, nameEn: 'Putrakaraka (PK)', nameHi: 'पुत्रकारक (PK)', sigEn: 'Children, Creative intelligence, Wisdom, Mantras & Future legacy', sigHi: 'संतान, सृजनात्मक प्रतिभा, विद्या, मंत्र साधना व भविष्य संचित कर्म' },
    { code: 'GK' as const, nameEn: 'Gnatikaraka (GK)', nameHi: 'ज्ञातिकारक (GK)', sigEn: 'Obstacles, Competitors, Diseases, Conflicts & Karmic debts', sigHi: 'विघ्न, प्रतिस्पर्धी, रोग-शत्रु, संघर्ष व कर्मिक ऋण शोधन' },
    { code: 'DK' as const, nameEn: 'Darakaraka (DK)', nameHi: 'दाराकारक (DK)', sigEn: 'Spouse, Life partner, Public dealings & Wealth partnership', sigHi: 'जीवनसाथी, वैवाहिक अनुबंध, जन-सरोकार व व्यावसायिक साझेदारी' }
  ];

  sortedPhysical.forEach((pl, idx) => {
    if (idx < jaiminiOrder.length) {
      jaiminiRoles[pl.name as PlanetName] = jaiminiOrder[idx];
    }
  });

  return planets
    .filter((p) => p.name !== 'Ascendant')
    .map((p) => {
      const pName = p.name as PlanetName;
      const deg = p.degreeInSign;
      const signIdx = p.signIndex;
      const sign = p.sign;
      const houseNum = p.house;
      const isRetrograde = !!p.isRetrograde;

      const deepInfo = DEEP_POINTS[pName];
      let isDeeplyExalted = false;
      let isDeeplyDebilitated = false;
      let deepDegreeNote: string | undefined;

      if (deepInfo) {
        if (sign === deepInfo.exaltSign && Math.abs(deg - deepInfo.exaltDeg) <= 3) {
          isDeeplyExalted = true;
          deepDegreeNote = lang === 'hi' ? `परमोच्च अंश (${deepInfo.exaltDeg}° के निकट)` : `Deep Exaltation (near ${deepInfo.exaltDeg}°)`;
        } else if (sign === deepInfo.debSign && Math.abs(deg - deepInfo.debDeg) <= 3) {
          isDeeplyDebilitated = true;
          deepDegreeNote = lang === 'hi' ? `परम नीच अंश (${deepInfo.debDeg}° के निकट)` : `Deep Debilitation (near ${deepInfo.debDeg}°)`;
        }
      }

      // Check Exaltation / Debilitation / Moolatrikona / Own House / Friends
      let calculatedDignity: DignityLevel = 'Neutral';
      let dignityScore = 50;

      if (deepInfo && sign === deepInfo.exaltSign) {
        calculatedDignity = 'Exalted';
        dignityScore = 100;
      } else if (deepInfo && sign === deepInfo.debSign) {
        calculatedDignity = 'Debilitated';
        dignityScore = 15;
      } else {
        const mt = MOOLATRIKONA[pName];
        if (mt && sign === mt.sign && deg >= mt.startDeg && deg <= mt.endDeg) {
          calculatedDignity = 'Moolatrikona';
          dignityScore = 90;
        } else if (ZODIAC_LORDS[sign] === pName) {
          calculatedDignity = 'Own House';
          dignityScore = 80;
        } else {
          // Check standard dignity from chart calculation
          if (p.dignity === 'Exalted') calculatedDignity = 'Exalted';
          else if (p.dignity === 'Debilitated') calculatedDignity = 'Debilitated';
          else if (p.dignity === 'Own House') calculatedDignity = 'Own House';
        }
      }

      // Neecha Bhanga Raja Yoga analysis for debilitated planets
      const isDebilitated = calculatedDignity === 'Debilitated';
      const isExalted = calculatedDignity === 'Exalted';
      
      const neechaReasons: string[] = [];
      let isNeechaBhangaActive = false;

      if (isDebilitated) {
        // 1. Check if the sign lord (dispositor) is in a Kendra (1, 4, 7, 10) from Lagna or Moon
        const dispositorName = ZODIAC_LORDS[sign];
        const dispositorPlanet = planets.find((item) => item.name === dispositorName);
        if (dispositorPlanet) {
          const dispHouseFromLagna = dispositorPlanet.house;
          const dispHouseFromMoon = ((dispositorPlanet.signIndex - (moonPlanet?.signIndex ?? 0) + 12) % 12) + 1;
          
          if ([1, 4, 7, 10].includes(dispHouseFromLagna)) {
            isNeechaBhangaActive = true;
            neechaReasons.push(`Sign Lord ${dispositorName} is placed in Kendra (H${dispHouseFromLagna}) from Lagna.`);
          } else if ([1, 4, 7, 10].includes(dispHouseFromMoon)) {
            isNeechaBhangaActive = true;
            neechaReasons.push(`Sign Lord ${dispositorName} is placed in Kendra (H${dispHouseFromMoon}) from Moon.`);
          }
          if (dispositorPlanet.dignity === 'Exalted') {
            isNeechaBhangaActive = true;
            neechaReasons.push(`Sign Lord ${dispositorName} is Exalted in the chart.`);
          }
        }

        // 2. Check if the planet that gets exalted in this debilitation sign is in Kendra from Lagna or Moon
        if (deepInfo) {
          const planetExaltedHere = Object.keys(DEEP_POINTS).find(
            (k) => DEEP_POINTS[k as PlanetName]?.exaltSign === sign
          ) as PlanetName | undefined;

          if (planetExaltedHere) {
            const exaltedHereObj = planets.find((item) => item.name === planetExaltedHere);
            if (exaltedHereObj && [1, 4, 7, 10].includes(exaltedHereObj.house)) {
              isNeechaBhangaActive = true;
              neechaReasons.push(`${planetExaltedHere} (which exalts in ${sign}) is in Kendra (H${exaltedHereObj.house}).`);
            }
          }
        }
      }

      const neechaBhanga = {
        isActive: isNeechaBhangaActive,
        reasons: neechaReasons,
        descriptionEn: isNeechaBhangaActive
          ? `Neecha Bhanga Raja Yoga is formed! Debilitation is cancelled and transmuted into strong rising potential.`
          : 'Debilitated placement requires mindful remedies and strengthening of significations.',
        descriptionHi: isNeechaBhangaActive
          ? `नीच भंग राजयोग निर्मित! नीचत्व समाप्त होकर यह ग्रह संघर्ष के उपरांत उच्च सफलता कारक बनेगा।`
          : 'नीच स्थिति होने से ग्रह से संबंधित भावों व कारकों में सावधानी व शांति उपाय अपेक्षित हैं।'
      };

      // Relationships calculation (Naisargika, Tatkalika, Panchadha Maitri)
      const dispositor = (ZODIAC_LORDS[sign] || 'Sun') as PlanetName;
      const lagnaLord = (ZODIAC_LORDS[houses[0]?.sign] || 'Mars') as PlanetName;

      const pNatural = NATURAL_FRIENDSHIPS[pName] || { friends: [], neutrals: [], enemies: [] };
      const naturalFriends = pNatural.friends;
      const naturalNeutrals = pNatural.neutrals;
      const naturalEnemies = pNatural.enemies;

      // Calculate temporal friends / enemies for this planet
      const temporalFriends: PlanetName[] = [];
      const temporalEnemies: PlanetName[] = [];

      planets
        .filter((other) => other.name !== 'Ascendant' && other.name !== pName)
        .forEach((other) => {
          const houseFromCurrent = ((other.house - houseNum + 12) % 12) + 1;
          const otherName = other.name as PlanetName;
          if ([2, 3, 4, 10, 11, 12].includes(houseFromCurrent)) {
            temporalFriends.push(otherName);
          } else {
            temporalEnemies.push(otherName);
          }
        });

      // Compound with Dispositor (House Lord of current placement)
      const natRelToDisp: 'Friend' | 'Neutral' | 'Enemy' = naturalFriends.includes(dispositor)
        ? 'Friend'
        : naturalEnemies.includes(dispositor)
        ? 'Enemy'
        : 'Neutral';
      const tempRelToDisp: 'Friend' | 'Enemy' = temporalFriends.includes(dispositor) ? 'Friend' : 'Enemy';
      const compoundWithDispositor = getPanchadhaMaitriCompound(natRelToDisp, tempRelToDisp);
      const compoundWithDispositorHi = getCompoundRelationshipHindi(compoundWithDispositor);

      // If calculatedDignity is still Neutral and not Exalted/Debilitated/Own/Moolatrikona, use Compound Dispositor dignity
      if (calculatedDignity === 'Neutral') {
        calculatedDignity = compoundWithDispositor;
        if (calculatedDignity === 'Great Friend') dignityScore = 70;
        else if (calculatedDignity === 'Friend') dignityScore = 60;
        else if (calculatedDignity === 'Neutral') dignityScore = 50;
        else if (calculatedDignity === 'Enemy') dignityScore = 35;
        else if (calculatedDignity === 'Great Enemy') dignityScore = 20;
      }

      // Lagna Lord Relationship
      const natRelToLagnaLord: 'Friend' | 'Neutral' | 'Enemy' =
        pName === lagnaLord
          ? 'Friend'
          : naturalFriends.includes(lagnaLord)
          ? 'Friend'
          : naturalEnemies.includes(lagnaLord)
          ? 'Enemy'
          : 'Neutral';
      const tempRelToLagnaLord: 'Friend' | 'Enemy' =
        pName === lagnaLord ? 'Friend' : temporalFriends.includes(lagnaLord) ? 'Friend' : 'Enemy';
      const compoundLagnaLord =
        pName === lagnaLord ? 'Great Friend' : getPanchadhaMaitriCompound(natRelToLagnaLord, tempRelToLagnaLord);
      const compoundLagnaLordHi =
        pName === lagnaLord ? 'स्वयं लग्नेश (परम हितैषी)' : getCompoundRelationshipHindi(compoundLagnaLord);

      // Karaka Info Assembly
      const sthiraObj = STHIRA_KARAKAS[pName] || STHIRA_KARAKAS.Sun;
      const jaiminiObj = jaiminiRoles[pName];

      const karaka: KarakaDetail = {
        sthiraNameEn: sthiraObj.karakaEn,
        sthiraNameHi: sthiraObj.karakaHi,
        sthiraSigEn: sthiraObj.sigEn,
        sthiraSigHi: sthiraObj.sigHi,
        jaiminiCode: jaiminiObj?.code,
        jaiminiNameEn: jaiminiObj?.nameEn,
        jaiminiNameHi: jaiminiObj?.nameHi,
        jaiminiSignificanceEn: jaiminiObj?.sigEn,
        jaiminiSignificanceHi: jaiminiObj?.sigHi
      };

      // Nature & Yogakaraka
      const { isFunctionalBenefic, isFunctionalMalefic, isYogakaraka, roleDescription } = getFunctionalNature(
        pName,
        lagnaSignIndex
      );

      const naturalMap: Record<PlanetName, 'Natural Benefic' | 'Natural Malefic'> = {
        Sun: 'Natural Malefic',
        Moon: 'Natural Benefic',
        Mars: 'Natural Malefic',
        Mercury: 'Natural Benefic',
        Jupiter: 'Natural Benefic',
        Venus: 'Natural Benefic',
        Saturn: 'Natural Malefic',
        Rahu: 'Natural Malefic',
        Ketu: 'Natural Malefic',
        Ascendant: 'Natural Benefic'
      };

      const naturalNature = naturalMap[pName] || 'Natural Benefic';
      const functionalNature: 'Functional Benefic' | 'Functional Malefic' | 'Neutral' = isFunctionalBenefic
        ? 'Functional Benefic'
        : isFunctionalMalefic
        ? 'Functional Malefic'
        : 'Neutral';

      const isLagnaLord = ZODIAC_LORDS[houses[0]?.sign] === pName;

      // Nakshatra Impact Analysis
      const nakIdx = p.nakshatraIndex ?? 0;
      const nakName = p.nakshatra || 'Ashwini';
      const meta = NAKSHATRA_METADATA[nakName] || NAKSHATRA_METADATA['Ashwini'];
      
      // Calculate Navtara from natal Moon
      const taraOffset = ((nakIdx - natalMoonNakIdx + 27) % 27) % 9;
      const taraObj = NAVTARA_LIST[taraOffset] || NAVTARA_LIST[0];

      const nakshatraImpact: NakshatraImpactDetail = {
        nakshatra: nakName,
        nakshatraIndex: nakIdx,
        pada: p.pada || 1,
        lord: p.nakshatraLord as PlanetName,
        deity: meta.deityEn,
        deityHi: meta.deityHi,
        shakti: meta.shaktiEn,
        shaktiHi: meta.shaktiHi,
        navtaraType: taraObj.nameEn,
        navtaraTypeHi: taraObj.nameHi,
        isTarabalaFavorable: taraObj.isFavorable,
        impactDescriptionEn: meta.coreThemeEn,
        impactDescriptionHi: meta.coreThemeHi
      };

      // Dignity Labels in EN & HI
      let dignityLabelEn: string = calculatedDignity;
      let dignityLabelHi: string = 'तटस्थ (सम)';

      if (calculatedDignity === 'Exalted') {
        dignityLabelEn = 'Exalted (Uccha)';
        dignityLabelHi = 'उच्च राशि (श्रेष्ठ)';
      } else if (calculatedDignity === 'Debilitated') {
        dignityLabelEn = isNeechaBhangaActive ? 'Debilitated (Neecha Bhanga Raja Yoga)' : 'Debilitated (Neecha)';
        dignityLabelHi = isNeechaBhangaActive ? 'नीच (नीच भंग राजयोग)' : 'नीच राशि (अशक्त)';
      } else if (calculatedDignity === 'Moolatrikona') {
        dignityLabelEn = 'Moolatrikona';
        dignityLabelHi = 'मूलत्रिकोण (अतिशुभ)';
      } else if (calculatedDignity === 'Own House') {
        dignityLabelEn = 'Own Sign (Swakshetra)';
        dignityLabelHi = 'स्वक्षेत्र (स्वराशि)';
      } else if (calculatedDignity === 'Great Friend') {
        dignityLabelEn = 'Great Friend (Adhi Mitra)';
        dignityLabelHi = 'अति मित्र (श्रेष्ठ संबंध)';
      } else if (calculatedDignity === 'Friend') {
        dignityLabelEn = 'Friend (Mitra)';
        dignityLabelHi = 'मित्र राशि (अनुकूल)';
      } else if (calculatedDignity === 'Neutral') {
        dignityLabelEn = 'Neutral (Sama)';
        dignityLabelHi = 'सम राशि (तटस्थ)';
      } else if (calculatedDignity === 'Enemy') {
        dignityLabelEn = 'Enemy (Shatru)';
        dignityLabelHi = 'शत्रु राशि (प्रतिकूल)';
      } else if (calculatedDignity === 'Great Enemy') {
        dignityLabelEn = 'Great Enemy (Adhi Shatru)';
        dignityLabelHi = 'अति शत्रु (कठिन संबंध)';
      }

      const moolatrikonaObj = MOOLATRIKONA[pName];
      const moolatrikonaInfo = moolatrikonaObj
        ? `${moolatrikonaObj.sign} (${moolatrikonaObj.startDeg}°–${moolatrikonaObj.endDeg}°)`
        : undefined;
      const moolatrikonaInfoHi = moolatrikonaObj
        ? `${moolatrikonaObj.sign} (${moolatrikonaObj.startDeg}° से ${moolatrikonaObj.endDeg}°)`
        : undefined;

      const exaltationInfo = deepInfo
        ? { sign: deepInfo.exaltSign, degree: deepInfo.exaltDeg }
        : { sign: 'Aries' as ZodiacSign, degree: 0 };
      const debilitationInfo = deepInfo
        ? { sign: deepInfo.debSign, degree: deepInfo.debDeg }
        : { sign: 'Libra' as ZodiacSign, degree: 0 };

      const relationships: PlanetaryRelationshipsInfo = {
        naturalFriends,
        naturalNeutrals,
        naturalEnemies,
        temporalFriends,
        temporalEnemies,
        dispositor,
        compoundWithDispositor,
        compoundWithDispositorHi,
        lagnaLord,
        lagnaLordRelationship: {
          natural: natRelToLagnaLord,
          temporal: tempRelToLagnaLord,
          compound: compoundLagnaLord,
          compoundHi: compoundLagnaLordHi
        },
        ownSigns: PLANET_OWN_SIGNS[pName] || [],
        moolatrikonaInfo,
        moolatrikonaInfoHi,
        exaltationInfo,
        debilitationInfo
      };

      // Match Shadbala item
      const planetShadbala = kundali.shadbala?.planets?.find((sb) => sb.planet === pName);

      return {
        planet: pName,
        sign,
        degreeInSign: deg,
        formattedDegree: p.degreeFormatted,
        house: houseNum,
        isRetrograde,
        dignity: calculatedDignity,
        dignityLabelEn,
        dignityLabelHi,
        dignityScore,
        deepDegreeNote,
        isDeeplyExalted,
        isDeeplyDebilitated,
        isDebilitated,
        isExalted,
        neechaBhanga,
        karaka,
        naturalNature,
        naturalNatureHi: naturalNature === 'Natural Benefic' ? 'नैसर्गिक शुभ' : 'नैसर्गिक पाप/क्रूर',
        functionalNature,
        functionalNatureHi:
          functionalNature === 'Functional Benefic'
            ? 'कार्यात्मक शुभ (योगद)'
            : functionalNature === 'Functional Malefic'
            ? 'कार्यात्मक अशुभ (अकारक)'
            : 'तटस्थ (सम)',
        isYogakaraka,
        isLagnaLord,
        natureRoleDescription: roleDescription,
        nakshatraImpact,
        relationships,
        shadbala: planetShadbala
      };
    });
}
